// src/pages/ITCallSearchDashboard.tsx — Call Search (Teams Calling)
//
// Replaces the legacy SSRS "Call Manager Search" admin tool: a per-call search
// over Teams call legs (external Direct Routing/PSTN + internal callRecord legs)
// for IT troubleshooting. Click a result row to expand the routing chain — every
// leg sharing the same call id (e.g. caller → queue → answering officer).
// Requires governed access to dashboards:it_call_search.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, SpinnerSize, TextField, ComboBox, IComboBoxOption, DatePicker,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  CaretLeft, ArrowsClockwise, MagnifyingGlass, Info,
  CaretDown, CaretRight, ArrowElbowDownRight,
} from '@phosphor-icons/react';
import { accessService } from '../services/accessService';

// ── Palette ──────────────────────────────────────────────────────────────────
const VIOLET = '#7C3AED';
const GREEN  = '#2e873d';
const RED    = '#D32F2F';

const VOICEMAIL_NOTE =
  'Answered currently includes calls picked up by voicemail; a voicemail split is planned.';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Leg {
  callId: string; correlationId: string | null;
  callSource: string; callScope: string; direction: string;
  caller: string; callee: string; calledParty: string; department: string;
  start: string; end: string; durationSeconds: number | null; outcome: string;
  trunkFqdn: string | null; sipCode: number | null; sipPhrase: string | null; subReason: string | null;
}
// The id that walks every leg of a call (transfers included); PSTN legs have no
// correlation id so their own call id is the best available key.
function chainKey(l: Leg): string { return l.correlationId || l.callId; }
interface SearchData { rows: Leg[]; meta: { dateFrom: number; dateTo: number; capped: number; generatedAt: string } }
interface ChainData { legs: Leg[]; meta: { callId: string } }
interface FilterOptions { calledParties: string[] }

// ── Helpers ───────────────────────────────────────────────────────────────────
function toDateKey(d: Date): number {
  return parseInt(d.toISOString().slice(0, 10).replace(/-/g, ''), 10);
}
function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) + ' ' +
    d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function fmtDur(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60); const s = sec % 60;
  return `${m}m ${s}s`;
}
function sourceLabel(src: string): string {
  if (src === 'direct_routing') return 'Direct Routing';
  if (src === 'pstn') return 'PSTN';
  if (src === 'callrecords_internal') return 'Teams internal';
  return src;
}
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });

// ── Styles (portal chrome) ────────────────────────────────────────────────────
const s = mergeStyleSets({
  page: {
    padding: 32, minHeight: 'calc(100vh - 52px)',
    background: '#F5F5F5', fontFamily: "'Segoe UI', system-ui, sans-serif",
    '@media (max-width:768px)': { padding: 16 },
  } as any,
  container: { maxWidth: 1280, margin: '0 auto' },
  headerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 20, flexWrap: 'wrap' as const, gap: 8,
  },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    color: VIOLET, fontWeight: 600, fontSize: 13, cursor: 'pointer',
    background: 'none', border: 'none', padding: 0, marginBottom: 4,
  },
  pageTitle: {
    fontSize: 26, fontWeight: 800, color: '#1A1A1A',
    letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10,
  },
  pageSub: { fontSize: 13, color: '#747474', marginTop: 2 },
  filterLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em',
  },
  filterGroup: { display: 'flex', flexDirection: 'column' as const, gap: 3 },
  searchBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 18px', background: VIOLET, border: `1px solid ${VIOLET}`,
    borderRadius: 6, fontSize: 13, fontWeight: 700, color: '#fff',
    cursor: 'pointer', alignSelf: 'flex-end',
    selectors: { ':hover': { background: '#6D28D9' }, ':disabled': { opacity: 0.6, cursor: 'default' } },
  } as any,
  clearBtn: {
    padding: '8px 14px', background: 'none', border: '1px solid #E1E1E1',
    borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#747474',
    cursor: 'pointer', alignSelf: 'flex-end',
    selectors: { ':hover': { borderColor: RED, color: RED } },
  } as any,
  sectionCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16,
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginBottom: 2 },
  sectionSub:   { fontSize: 12, color: '#9CA3AF', marginBottom: 14 },
  th: {
    textAlign: 'left' as const, padding: '8px 10px', fontWeight: 700,
    fontSize: 11, color: '#747474', background: '#F9FAFB',
    borderBottom: '1px solid #E1E1E1', whiteSpace: 'nowrap' as const,
  },
  td: { padding: '7px 10px', borderBottom: '1px solid #F3F4F6', color: '#232323', fontSize: 12, verticalAlign: 'top' as const },
  centeredMsg: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
    justifyContent: 'center', minHeight: 200, gap: 12,
    color: '#747474', fontSize: 14, textAlign: 'center' as const,
  },
  accessBox: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: 56, textAlign: 'center' as const, color: '#747474',
  },
  accessTitle: { fontWeight: 700, fontSize: 18, color: '#1A1A1A', marginBottom: 8 },
  pill: {
    display: 'inline-block', padding: '1px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' as const,
  },
});

function OutcomePill({ outcome }: { outcome: string }) {
  if (outcome === 'Answered') {
    return (
      <span className={s.pill} style={{ background: '#F0FDF4', color: GREEN }}>
        Answered<span title={VOICEMAIL_NOTE} style={{ cursor: 'help', color: '#D97706' }}> ⓘ</span>
      </span>
    );
  }
  if (outcome === 'Abandoned') {
    return <span className={s.pill} style={{ background: '#FEF2F2', color: RED }}>Abandoned</span>;
  }
  return <span className={s.pill} style={{ background: '#F3F4F6', color: '#6B7280' }}>—</span>;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ITCallSearchDashboard() {
  const nav = useNavigate();

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  // Form state (defaults: last 7 days, everything else open)
  const week = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d; };
  const [dateFrom,     setDateFrom]     = useState<Date | undefined>(week());
  const [dateTo,       setDateTo]       = useState<Date | undefined>(new Date());
  const [timeFrom,     setTimeFrom]     = useState('');
  const [timeTo,       setTimeTo]       = useState('');
  const [callerBegins, setCallerBegins] = useState('');
  const [callerEnds,   setCallerEnds]   = useState('');
  const [calleeEnds,   setCalleeEnds]   = useState('');
  const [calledParty,  setCalledParty]  = useState('');
  const [durationLt,   setDurationLt]   = useState('');

  const [filterOpts, setFilterOpts] = useState<FilterOptions | null>(null);
  const [results,    setResults]    = useState<SearchData | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  // Chain expander
  const [openChain,     setOpenChain]     = useState<string | null>(null);   // callId of the expanded row
  const [chain,         setChain]         = useState<ChainData | null>(null);
  const [chainLoading,  setChainLoading]  = useState(false);

  // ── Access gate ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedDashboards?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        setHasAccess(d.role === 'admin'
          ? true
          : Array.isArray(d.allowedDashboards) && d.allowedDashboards.includes('it_call_search'));
      } catch { setHasAccess(false); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!hasAccess) return;
    accessService.get<FilterOptions>('/it-call-search/filter-options')
      .then(r => setFilterOpts(r.data))
      .catch(e => console.warn('[IT-CallSearch] filter-options failed:', e.message));
  }, [hasAccess]);

  const timeFromBad = timeFrom !== '' && !TIME_RE.test(timeFrom);
  const timeToBad   = timeTo   !== '' && !TIME_RE.test(timeTo);

  const runSearch = async () => {
    if (timeFromBad || timeToBad) return;
    setLoading(true); setError(null); setOpenChain(null); setChain(null);
    try {
      const params: Record<string, string | number> = {
        dateFrom: dateFrom ? toDateKey(dateFrom) : '',
        dateTo:   dateTo   ? toDateKey(dateTo)   : '',
      };
      if (timeFrom)     params.timeFrom = timeFrom;
      if (timeTo)       params.timeTo = timeTo;
      if (callerBegins) params.callerBegins = callerBegins;
      if (callerEnds)   params.callerEnds = callerEnds;
      if (calleeEnds)   params.calleeEnds = calleeEnds;
      if (calledParty)  params.calledParty = calledParty;
      if (durationLt && Number(durationLt) > 0) params.durationLt = Number(durationLt);
      const r = await accessService.get<SearchData>('/it-call-search/search', { params });
      setResults(r.data);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setDateFrom(week()); setDateTo(new Date());
    setTimeFrom(''); setTimeTo('');
    setCallerBegins(''); setCallerEnds(''); setCalleeEnds('');
    setCalledParty(''); setDurationLt('');
    setResults(null); setOpenChain(null); setChain(null); setError(null);
  };

  const toggleChain = async (callId: string) => {
    if (openChain === callId) { setOpenChain(null); setChain(null); return; }
    setOpenChain(callId); setChain(null); setChainLoading(true);
    try {
      const r = await accessService.get<ChainData>('/it-call-search/chain', { params: { callId } });
      setChain(r.data);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to load call chain');
      setOpenChain(null);
    } finally {
      setChainLoading(false);
    }
  };

  const partyOptions: IComboBoxOption[] = React.useMemo(
    () => (filterOpts?.calledParties ?? []).map(n => ({ key: n, text: n })),
    [filterOpts],
  );

  // ── Access gate render ───────────────────────────────────────────────────────
  if (hasAccess === null) {
    return (
      <div className={s.page}>
        <div className={s.container} style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
          <Spinner size={SpinnerSize.large} label="Checking access…" />
        </div>
      </div>
    );
  }
  if (hasAccess === false) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <button className={s.backBtn} onClick={() => nav('/dashboards')}>
            <CaretLeft size={12} weight="bold" /> Dashboards
          </button>
          <div className={s.accessBox}>
            <div className={s.accessTitle}>Access Required</div>
            <div style={{ fontSize: 14, color: '#747474', maxWidth: 460, margin: '0 auto' }}>
              Call Search requires governed access.
              Submit an access request to your administrator to get started.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.container}>

        <button className={s.backBtn} onClick={() => nav('/dashboards')}>
          <CaretLeft size={12} weight="bold" /> Dashboards
        </button>

        {/* Header */}
        <div className={s.headerRow}>
          <div>
            <div className={s.pageTitle}>
              <span style={{
                width: 36, height: 36, borderRadius: 8, background: VIOLET,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <MagnifyingGlass size={18} color="#fff" weight="bold" />
              </span>
              Call Search
            </div>
            <div className={s.pageSub}>Teams calling — call detail search</div>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12, borderRadius: 8 } }}>
            {error}
          </MessageBar>
        )}

        {/* Search form */}
        <div style={{
          background: '#fff', border: '1px solid #E1E1E1', borderRadius: 8,
          padding: '14px 20px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>From date</span>
              <DatePicker value={dateFrom} onSelectDate={d => setDateFrom(d ?? undefined)}
                formatDate={d => d ? d.toLocaleDateString('en-AU') : ''}
                styles={{ root: { width: 140 } }} />
            </div>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>To date</span>
              <DatePicker value={dateTo} onSelectDate={d => setDateTo(d ?? undefined)}
                formatDate={d => d ? d.toLocaleDateString('en-AU') : ''}
                styles={{ root: { width: 140 } }} />
            </div>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>From time (HH:MM)</span>
              <TextField value={timeFrom} onChange={(_, v) => setTimeFrom(v ?? '')}
                placeholder="e.g. 08:30" errorMessage={timeFromBad ? '24h HH:MM' : undefined}
                styles={{ root: { width: 120 } }} />
            </div>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>To time (HH:MM)</span>
              <TextField value={timeTo} onChange={(_, v) => setTimeTo(v ?? '')}
                placeholder="e.g. 17:00" errorMessage={timeToBad ? '24h HH:MM' : undefined}
                styles={{ root: { width: 120 } }} />
            </div>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>Duration less than (sec)</span>
              <TextField value={durationLt} onChange={(_, v) => setDurationLt((v ?? '').replace(/[^\d]/g, ''))}
                placeholder="e.g. 10" styles={{ root: { width: 140 } }} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>Caller number begins with</span>
              <TextField value={callerBegins} onChange={(_, v) => setCallerBegins(v ?? '')}
                placeholder="e.g. +6139" styles={{ root: { width: 170 } }} />
            </div>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>Caller number ends with</span>
              <TextField value={callerEnds} onChange={(_, v) => setCallerEnds(v ?? '')}
                placeholder="e.g. 4402" styles={{ root: { width: 170 } }} />
            </div>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>Destination number ends with</span>
              <TextField value={calleeEnds} onChange={(_, v) => setCalleeEnds(v ?? '')}
                placeholder="e.g. 4444" styles={{ root: { width: 190 } }} />
            </div>
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>Called party</span>
              <ComboBox
                placeholder="All"
                options={partyOptions}
                selectedKey={calledParty || undefined}
                allowFreeform={false}
                autoComplete="on"
                onChange={(_, o) => setCalledParty(o ? String(o.key) : '')}
                styles={{ root: { width: 250 } }} />
            </div>
            <button className={s.searchBtn} onClick={runSearch} disabled={loading || timeFromBad || timeToBad}>
              {loading
                ? <ArrowsClockwise size={14} weight="bold" style={{ animation: `${spinAnim} 1s linear infinite` }} />
                : <MagnifyingGlass size={14} weight="bold" />}
              Search
            </button>
            <button className={s.clearBtn} onClick={clearForm}>Clear</button>
          </div>
        </div>

        {/* Methodology strip */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 12,
          fontSize: 12, color: '#6B7280', background: '#F9FAFB', border: '1px solid #E5E7EB',
          borderRadius: 6, padding: '8px 12px', lineHeight: 1.5,
        }}>
          <Info size={14} color="#6B7280" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong>What this is:</strong> a call-detail search across Microsoft Teams calling records —
            external calls (via the phone network) and internal staff-to-staff calls — for IT
            troubleshooting. Click a result to walk the whole call: one <strong>correlation id</strong>{' '}
            links every record of a call, including transfers, across the external and internal feeds
            (for example caller → queue → answering officer). For tracing:{' '}
            <strong>SBC / Trunk</strong> shows how the call was received, <strong>SIP</strong> is the
            state the call ended in (hover for the phrase), and <strong>Subcode</strong> indicates who or
            what ended, transferred or errored the call. Answered figures marked ⓘ currently include
            voicemail pickups. Data refreshes daily around 5 AM (the legacy tool updated every two hours),
            so today's calls appear tomorrow; the warehouse keeps the full history.
          </span>
        </div>

        {/* Results */}
        {loading && (
          <div className={s.centeredMsg}>
            <Spinner size={SpinnerSize.large} />
            <span>Searching…</span>
          </div>
        )}

        {!loading && !results && (
          <div className={s.centeredMsg} style={{ minHeight: 160 }}>
            <MagnifyingGlass size={24} color="#9CA3AF" />
            <span>Set your criteria and press <strong>Search</strong> to find calls.</span>
          </div>
        )}

        {!loading && results && (
          <div className={s.sectionCard}>
            <div className={s.sectionTitle}>Results</div>
            <div className={s.sectionSub}>
              {results.rows.length === 0 ? 'No calls match' : `${results.rows.length} call record${results.rows.length === 1 ? '' : 's'}`}
              {results.rows.length >= results.meta.capped ? ` (showing the newest ${results.meta.capped} — narrow the search for more)` : ''}
              {' '}· newest first · click a row to see the linked call records
            </div>
            {results.rows.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th className={s.th}></th>
                      <th className={s.th}>Start</th>
                      <th className={s.th}>Direction</th>
                      <th className={s.th}>Caller</th>
                      <th className={s.th}>Called number</th>
                      <th className={s.th}>Called party</th>
                      <th className={s.th}>Department</th>
                      <th className={s.th}>Source</th>
                      <th className={s.th}>Duration</th>
                      <th className={s.th}>Outcome</th>
                      <th className={s.th} title="How the call was received — the receiving SBC/trunk (the legacy 'Original Called Device Name')" style={{ cursor: 'help' }}>SBC / Trunk</th>
                      <th className={s.th} title="Final SIP code — the state the call ended in (the legacy 'Cause Value'). Hover a value for the full phrase." style={{ cursor: 'help' }}>SIP</th>
                      <th className={s.th} title="Microsoft subcode — who or what ended, transferred or errored the call (the legacy 'Termination On Behalf Of')" style={{ cursor: 'help' }}>Subcode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.rows.map((r, i) => (
                      <React.Fragment key={`${r.callId}-${i}`}>
                        <tr style={{ cursor: 'pointer', background: openChain === chainKey(r) ? '#F5F3FF' : undefined }}
                          onClick={() => toggleChain(chainKey(r))}>
                          <td className={s.td}>{openChain === chainKey(r) ? <CaretDown size={12} /> : <CaretRight size={12} />}</td>
                          <td className={s.td} style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.start)}</td>
                          <td className={s.td}>{r.direction}</td>
                          <td className={s.td} style={{ fontWeight: 600 }}>{r.caller}</td>
                          <td className={s.td}>{r.callee}</td>
                          <td className={s.td}>{r.calledParty}</td>
                          <td className={s.td}>{r.department}</td>
                          <td className={s.td}>{sourceLabel(r.callSource)}{r.callScope ? ` · ${r.callScope}` : ''}</td>
                          <td className={s.td} style={{ whiteSpace: 'nowrap' }}>{fmtDur(r.durationSeconds)}</td>
                          <td className={s.td}><OutcomePill outcome={r.outcome} /></td>
                          <td className={s.td}>
                            {r.trunkFqdn
                              ? <span title={r.trunkFqdn} style={{ display: 'inline-block', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{r.trunkFqdn}</span>
                              : '—'}
                          </td>
                          <td className={s.td}>
                            {r.sipCode != null
                              ? <span title={r.sipPhrase || undefined} style={{ cursor: r.sipPhrase ? 'help' : undefined, fontWeight: 600, color: r.sipCode === 200 ? GREEN : '#B45309' }}>{r.sipCode}</span>
                              : '—'}
                          </td>
                          <td className={s.td}>{r.subReason || '—'}</td>
                        </tr>
                        {openChain === chainKey(r) && (
                          <tr>
                            <td className={s.td} colSpan={13} style={{ background: '#FAFAF9', padding: '10px 14px' }}>
                              {chainLoading && <Spinner size={SpinnerSize.small} label="Loading call chain…" labelPosition="right" />}
                              {!chainLoading && chain && chain.meta.callId === chainKey(r) && (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                                    Call chain · {chain.legs.length} record{chain.legs.length === 1 ? '' : 's'} · correlation id <code style={{ fontSize: 10.5 }}>{chainKey(r)}</code>
                                  </div>
                                  {chain.legs.map((l, j) => (
                                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12, flexWrap: 'wrap' }}>
                                      <ArrowElbowDownRight size={13} color={VIOLET} style={{ marginLeft: j * 14, flexShrink: 0 }} />
                                      <span style={{ color: '#9CA3AF', whiteSpace: 'nowrap' }}>{fmtWhen(l.start)}</span>
                                      <span style={{ fontWeight: 600 }}>{l.caller}</span>
                                      <span style={{ color: '#9CA3AF' }}>→</span>
                                      <span style={{ fontWeight: 600 }}>{l.calledParty !== '—' ? l.calledParty : l.callee}</span>
                                      <span style={{ color: '#6B7280' }}>
                                        ({l.direction} · {sourceLabel(l.callSource)} · {fmtDur(l.durationSeconds)}
                                        {l.trunkFqdn ? <> · via <span title={l.trunkFqdn}>{l.trunkFqdn.split('.')[0]}…</span></> : null}
                                        {l.sipCode != null ? <> · SIP <span title={l.sipPhrase || undefined} style={{ cursor: l.sipPhrase ? 'help' : undefined }}>{l.sipCode}</span></> : null}
                                        {l.subReason ? ` · sub ${l.subReason}` : ''})
                                      </span>
                                      <OutcomePill outcome={l.outcome} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
